import React, { useCallback, useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { ROLES, useUserRole, hasRole } from '../lib/auth/roles';
import { listInterventionWaitlist } from '../lib/functions';
import { Icons } from '../lib/Icons';

export default function StudentInterventionWaitlistPanel({ studentId }) {
  const { role } = useUserRole();
  const canEdit = hasRole(role, [ROLES.SENCO]);
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    if (!studentId) return;
    setLoading(true);
    try {
      const result = await listInterventionWaitlist({ studentId });
      setEntries(Array.isArray(result.data?.entries) ? result.data.entries : []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [studentId]);

  useEffect(() => {
    load();
  }, [load]);

  if (loading) {
    return <p className="text-sm text-gray-500">Loading waitlist...</p>;
  }

  if (entries.length === 0) return null;

  return (
    <Card className="border-purple-200 dark:border-purple-800">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm flex items-center gap-2">
          <Icons.Clock className="w-4 h-4 text-purple-600" />
          Intervention waitlist
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <p className="text-xs text-gray-600 dark:text-gray-400">
          {canEdit
            ? 'Groups may be full when Section F provision is accepted. Pupils are prioritised by statutory need and time waiting.'
            : 'This pupil is waiting for a place in the following intervention groups.'}
        </p>
        {entries.map((entry) => (
          <div
            key={entry.id}
            className="rounded-lg border border-purple-100 bg-purple-50/50 p-3 dark:border-purple-900 dark:bg-purple-950/20"
          >
            <div className="flex flex-wrap items-start justify-between gap-2">
              <div>
                <p className="font-medium text-gray-900 dark:text-white">
                  #{entry.queuePosition}: {entry.suggestedName || entry.interventionType}
                </p>
                <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                  {entry.interventionType}
                  {entry.estimatedFrequency ? ` • ${entry.estimatedFrequency}` : ''}
                  {entry.estimatedDuration ? ` • ${entry.estimatedDuration}` : ''}
                </p>
                {entry.priorityReason && (
                  <p className="text-xs text-purple-700 dark:text-purple-300 mt-1">{entry.priorityReason}</p>
                )}
                {entry.sourceText && (
                  <p className="text-xs text-gray-500 mt-1 line-clamp-2">EHCP: {entry.sourceText}</p>
                )}
              </div>
              {entry.hasEhcp && (
                <span className="inline-flex px-2 py-0.5 text-xs rounded-full bg-emerald-100 text-emerald-800">
                  EHCP
                </span>
              )}
            </div>
          </div>
        ))}
      </CardContent>
    </Card>
  );
}
